import { useEffect, type ComponentType } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import type { PageKey } from "@owners/shared-types";
import { useMe, usePrefs } from "./api";
import { PATHS } from "./routes";
import { Shell } from "./components/Shell";
import { SignIn } from "./pages/SignIn";
import { TodaysUpdate } from "./pages/TodaysUpdate";
import { AssetManagement } from "./pages/AssetManagement";
import { Settings } from "./pages/Settings";
import { AdminPanel } from "./pages/AdminPanel";
import { Placeholder } from "./pages/Placeholder";
import { Dataroom } from "./pages/Dataroom";
import { MissingData } from "./pages/MissingData";
import { FolderStatus } from "./pages/FolderStatus";
import { FlashReports } from "./pages/FlashReports";
import { FinancialReports } from "./pages/FinancialReports";
import { DebtCompliance } from "./pages/DebtCompliance";
import { BudgetReforecast } from "./pages/BudgetReforecast";
import { MyReports } from "./pages/MyReports";
import { Trends } from "./pages/Trends";
import { ReceiverReports } from "./pages/ReceiverReports";
import { Receivership } from "./pages/Receivership";
import { RequestRecords } from "./pages/RequestRecords";
import { TeamMembers } from "./pages/TeamMembers";
import { GuestReviews } from "./pages/GuestReviews";
import { AwaitingApproval } from "./pages/AwaitingApproval";
import { Leadership } from "./pages/Leadership";

const BUILT: Partial<Record<PageKey, ComponentType>> = {
  todays_update: TodaysUpdate,
  flash_reports: FlashReports,
  financial_reports: FinancialReports,
  debt_compliance: DebtCompliance,
  budget_reforecast: BudgetReforecast,
  my_reports: MyReports,
  trends: Trends,
  receiver_reports: ReceiverReports,
  receivership: Receivership,
  request_records: RequestRecords,
  team_members: TeamMembers,
  guest_reviews: GuestReviews,
  awaiting_approval: AwaitingApproval,
  leadership: Leadership,
  asset_management: AssetManagement,
  settings: Settings,
  admin_panel: AdminPanel,
  dataroom: Dataroom,
  missing_data: MissingData,
  reo_documents: () => <FolderStatus folder="reo_documents" />,
  compliance: () => <FolderStatus folder="compliance" />,
};

export function App() {
  const { data: me, isLoading, error } = useMe();
  const loc = useLocation();

  if (isLoading) return <div className="main muted">Loading…</div>;
  if (error) return <div className="main error">Couldn't reach the server. Refresh to try again.</div>;
  if (!me) {
    return (
      <Routes>
        <Route path="/sign-in" element={<SignIn />} />
        <Route path="*" element={<Navigate to="/sign-in" replace state={{ from: loc.pathname }} />} />
      </Routes>
    );
  }

  return (
    <Shell me={me}>
      <StartPage pages={me.pages} />
      <Routes>
        {/* Admins reach the Admin panel whatever level they preview. */}
        {me.level === "admin" && <Route path={PATHS.admin_panel} element={<AdminPanel />} />}
        {me.pages.filter((p) => p !== "admin_panel").map((p) => {
          const Page = BUILT[p];
          return <Route key={p} path={PATHS[p]} element={Page ? <Page /> : <Placeholder page={p} />} />;
        })}
        <Route path="/sign-in" element={<Navigate to="/" replace />} />
        <Route path="*" element={<NotAllowed />} />
      </Routes>
    </Shell>
  );
}

/**
 * Settings → Default views → Start page. Applies once per app load, only when
 * landing on "/" (after sign-in or opening the site), so the logo and the
 * Today's Update link still go to Today's Update afterwards.
 */
let landed = false;
function StartPage({ pages }: { pages: PageKey[] }) {
  const prefs = usePrefs();
  const loc = useLocation();
  const nav = useNavigate();
  useEffect(() => {
    if (landed || prefs.isLoading) return;
    landed = true;
    const home = prefs.data?.home;
    if (loc.pathname === "/" && home && home !== "todays_update" && pages.includes(home)) nav(PATHS[home], { replace: true });
  }, [prefs.isLoading, prefs.data, loc.pathname, pages, nav]);
  return null;
}

function NotAllowed() {
  return (
    <div className="card">
      <h2>This page isn't available</h2>
      <p className="muted">Either it doesn't exist or your access level can't open it.</p>
    </div>
  );
}
